// Talks to Supabase directly over its REST API (PostgREST) rather than via
// the @supabase/supabase-js client library. The library's installed version
// doesn't correctly support the newer "sb_publishable_..." key format yet,
// which caused every save to fail silently. Plain fetch() calls work fine
// with this key and avoid the dependency + version-compatibility question
// entirely.
const SUPABASE_URL = 'https://bfdrvvvoduutrnoedzmg.supabase.co';
const SUPABASE_KEY = 'sb_publishable_rzom7c4U-JM_UV3xD_ev-g_9ZlyxVfw';
const ROW_ID = 'koo-yong';

const KEY = 'koo-yong-v1'; // kept as a local cache / offline fallback only

const defaultState = {
  plants: [],
  tasks: [],
  costs: [],
  journalEntries: [],
  treeMarkers: [],
  treeMapBackground: null,
  members: [
    { id: 'm1', name: 'Owner', initials: 'KY', color: '#3D6B22' }
  ]
};

function loadLocalCache() {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return null;
}

function saveLocalCache(state) {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch (e) {}
}

async function fetchRemote() {
  const res = await fetch(
    `${SUPABASE_URL}/rest/v1/garden_state?id=eq.${ROW_ID}&select=data`,
    { headers: { apikey: SUPABASE_KEY, Authorization: `Bearer ${SUPABASE_KEY}` } }
  );
  if (!res.ok) throw new Error('Supabase fetch failed: ' + res.status);
  const json = await res.json();
  return json[0]?.data || {};
}

async function patchRemote(state) {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/garden_state?id=eq.${ROW_ID}`, {
    method: 'PATCH',
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${SUPABASE_KEY}`,
      'Content-Type': 'application/json',
      Prefer: 'return=minimal',
    },
    body: JSON.stringify({ data: state, updated_at: new Date().toISOString() }),
  });
  if (!res.ok) throw new Error('Supabase update failed: ' + res.status);
}

// Loads the shared garden state from Supabase (the real source of truth).
// Falls back to whatever's cached in this browser's localStorage if the
// network/Supabase is unreachable, so the app still works offline-ish.
// On first-ever run (empty Supabase row), seeds Supabase from any existing
// local data so nothing already in this browser gets lost in the migration.
export async function loadStore() {
  const localData = loadLocalCache();

  try {
    const remoteData = await fetchRemote();
    const remoteIsEmpty = !remoteData || Object.keys(remoteData).length === 0;

    if (remoteIsEmpty && localData) {
      const merged = { ...defaultState, ...localData };
      await patchRemote(merged);
      saveLocalCache(merged);
      return merged;
    }

    const merged = { ...defaultState, ...remoteData };
    saveLocalCache(merged);
    return merged;
  } catch (e) {
    console.error('Koo Yong: could not reach Supabase, using local cache', e);
    return localData ? { ...defaultState, ...localData } : defaultState;
  }
}

// Debounced so rapid successive edits (e.g. fast typing) collapse into one
// network write instead of one per keystroke. The local cache updates
// immediately regardless, so nothing is lost even if the network call fails.
let saveTimer = null;
export function saveStore(state) {
  saveLocalCache(state);
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    try {
      await patchRemote(state);
    } catch (e) {
      console.error('Koo Yong: failed to save to Supabase (saved locally only)', e);
    }
  }, 400);
}

export function getDefaultState() {
  return defaultState;
}
